import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Coins } from '@phosphor-icons/react';

import {
  createFeeRule,
  deleteFeeRule,
  listFeeRules,
  updateFeeRule,
  type FeeRule,
  type FeeRuleInput,
} from '@/api/notices';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useConfirm } from '@/components/ConfirmDialog';

interface Props {
  slug: string;
  member: { id: string; name: string };
  teamId: string;
  teamName: string;
  period: string;
  onClose: () => void;
  onSaved: () => void;
}

// MemberFeeOverrideSheet edits a single member's fee override for one team — the
// old system's "Redigera kostnader". It reads/writes a person-level fee rule
// (level 'override', scoped to member_id + team_id): a custom training amount
// and/or membership/training waivers. This is the most-specific layer the fee
// engine resolves, so it wins over category and club defaults for that member.
export function MemberFeeOverrideSheet({
  slug,
  member,
  teamId,
  teamName,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const confirm = useConfirm();

  const rulesQuery = useQuery({
    queryKey: ['notices-fee-rules', slug],
    queryFn: () => listFeeRules(token, slug),
    enabled: !!token,
  });

  // The existing override for this member+team, if any.
  const existing = useMemo<FeeRule | undefined>(
    () =>
      rulesQuery.data?.fee_rules.find(
        (r) => r.level === 'override' && r.member_id === member.id && r.team_id === teamId,
      ),
    [rulesQuery.data, member.id, teamId],
  );

  const [trainingMajor, setTrainingMajor] = useState('');
  const [waiveMembership, setWaiveMembership] = useState(false);
  const [waiveTraining, setWaiveTraining] = useState(false);
  const [note, setNote] = useState('');
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (seeded || rulesQuery.isLoading) return;
    if (existing) {
      setTrainingMajor(
        existing.training_minor != null ? String(existing.training_minor / 100) : '',
      );
      setWaiveMembership(existing.exempt_membership);
      setWaiveTraining(existing.exempt_training);
      setNote(existing.note ?? '');
    }
    setSeeded(true);
  }, [seeded, rulesQuery.isLoading, existing]);

  function buildInput(): FeeRuleInput {
    const trimmed = trainingMajor.trim();
    const trainingMinor =
      trimmed !== '' && !waiveTraining ? Math.round(Number(trimmed) * 100) : null;
    return {
      level: 'override',
      member_id: member.id,
      team_id: teamId,
      training_minor: Number.isFinite(trainingMinor as number) ? trainingMinor : null,
      exempt_membership: waiveMembership,
      exempt_training: waiveTraining,
      note: note.trim() || null,
      reason: 'admin_override',
    };
  }

  const saveM = useMutation({
    mutationFn: async () => {
      const body = buildInput();
      if (existing) await updateFeeRule(token, slug, existing.id, body);
      else await createFeeRule(token, slug, body);
    },
    onSuccess: onSaved,
  });

  const removeM = useMutation({
    mutationFn: () => deleteFeeRule(token, slug, existing!.id),
    onSuccess: onSaved,
  });

  async function onRemove() {
    if (!existing) return;
    const ok = await confirm({
      title: t('notices.members.fees.removeTitle'),
      body: t('notices.members.fees.removeBody', { name: member.name }),
      confirmLabel: t('notices.members.fees.remove'),
      tone: 'destructive',
    });
    if (ok) removeM.mutate();
  }

  const busy = saveM.isPending || removeM.isPending;

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[24rem]">
        <div className="flex shrink-0 items-center gap-2 border-b border-border px-5 py-4">
          <Coins size={18} className="text-muted-foreground" />
          <SheetTitle className="font-heading text-base font-bold">
            {t('notices.members.fees.title')}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {t('notices.members.fees.description')}
          </SheetDescription>
        </div>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
          <div className="flex flex-col gap-0.5">
            <span className="font-heading text-sm font-semibold">{member.name}</span>
            <span className="text-sm text-foreground/60">{teamName}</span>
          </div>

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t('notices.members.fees.trainingAmount')}</span>
            <input
              type="number"
              min={0}
              step="1"
              inputMode="decimal"
              value={trainingMajor}
              disabled={waiveTraining}
              onChange={(e) => setTrainingMajor(e.target.value)}
              placeholder={t('notices.members.fees.trainingPlaceholder')}
              className="min-h-[var(--tap)] rounded-pill border border-border bg-background px-4 disabled:opacity-50"
            />
            <span className="text-xs text-foreground/55">
              {t('notices.members.fees.trainingHint')}
            </span>
          </label>

          <label className="flex min-h-[var(--tap)] cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={waiveTraining}
              onChange={(e) => setWaiveTraining(e.target.checked)}
            />
            <span className="font-medium">{t('notices.members.fees.waiveTraining')}</span>
          </label>
          <label className="flex min-h-[var(--tap)] cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={waiveMembership}
              onChange={(e) => setWaiveMembership(e.target.checked)}
            />
            <span className="font-medium">{t('notices.members.fees.waiveMembership')}</span>
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t('notices.members.fees.note')}</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="rounded-xl border border-border bg-background px-3 py-2"
            />
          </label>

          {saveM.isError && (
            <p className="text-sm text-destructive">{t('notices.members.fees.saveFailed')}</p>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border p-4">
          {existing ? (
            <Button variant="outline" disabled={busy} onClick={() => void onRemove()}>
              {t('notices.members.fees.remove')}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" disabled={busy} onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button disabled={busy} onClick={() => saveM.mutate()}>
              {saveM.isPending ? t('notices.members.fees.saving') : t('notices.members.fees.save')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
